import { List, Map } from "immutable";
import i18next from "i18next";
import { createSelector } from "reselect";
import { getUserNameOrEmail } from "helpers/getUserUIData";
import { escapeSpecialSymbols } from "helpers/patternsRegExp.ts";
import { getPublishedProTracksLibrariesList, getPublishedProtracksList } from "store/entities/protracks/selectors";
import { organizationId as editingOrganizationIdFromRouter } from "store/entities/router/selectors";
import { USERS_FILTER } from "app/constants/organizations";
import { invitesState } from "./reducer";
import { CountriesCodes, ReviewsLinks } from "store/records/organization";
import { USA_ISO } from "app/constants/phoneCountryCodes";
import { getOrganizationSettingsChildrenList } from "store/entities/organizationSettings/selectors";
import {
  filterBySearh,
  filterBySort,
  filterByTeam,
  filterUsersNotInvitationAccepted,
  filterUsersNotProTrackPicked,
  handleAdminsListFiltered,
  handleTotalPages,
  handleUsersForCurrentPage,
  takeEmailsUsersFromUsersList
} from "helpers/selectorHelpers";
import {
  ACTIVITY_DEFAULT_IMAGE_DOMAIN,
  ACTIVITY_DEFAULT_IMAGE_DOMAIN_ID,
  ACTIVITY_DEFAULT_IMAGE_MESSAGE,
  ACTIVITY_DEFAULT_IMAGE_MESSAGE_ID
} from "app/constants/activity";

export const getOrganizations = state => state.get('persist').organizations

export const getPendingObject = createSelector(getOrganizations, organization => organization.get('pendingObject'))

export const getAllOrganizations = createSelector(getOrganizations, organizations => organizations.get('all') || Map())

export const isOrganizationDataLoaded = orgId => {
  return createSelector(getAllOrganizations, all => all.getIn([orgId, 'loaded']))
}

export const getAllOrganizationsLabelIds = createSelector(getAllOrganizations, organizations =>
  organizations.reduce((labelIds, organization) => {
    return organization.get('labelId') ? labelIds.push(organization.get('labelId')) : labelIds
  }, List())
)

export const getOrganizationsList = createSelector(getAllOrganizations, organizations =>
  organizations.reduce((all, organization) => all.push(organization), List())
)

export const getOrganizationsWithMutliAdminsList = createSelector(getOrganizationsList, organizations => {
  return organizations.filter(organization => {
    return organization.get('isChildrenOrganizationExists') && organization.get('isMultiAdminExists')
  })
})

export const getFirstOrganization = createSelector(getOrganizationsList, organizations => organizations.first())

export const getViewOrganizationId = createSelector(getOrganizations, organizations => organizations.get('view'))

/**
 * Current organization. Used for Main Header Logo for example
 */

export const getViewOrganization = createSelector(getAllOrganizations, getViewOrganizationId, (organizations, id) =>
  organizations.get(id.toString())
)

export const getViewOrganizationProtracks = createSelector(getViewOrganization, organization => {
  const proTracksList = organization.get('proTracksLevels').map(level => level.get('proTrack'))
  return proTracksList.toSet().toList()
})

export const getViewOrganizationReviewsLinks = createSelector(getViewOrganization, organization => {
  return organization.get('reviewsLinks')
})

export const getOrganizationsPagination = createSelector(getOrganizations, organizations =>
  organizations.get('pagination')
)

export const getOrganizationsCurrentPage = createSelector(getOrganizationsPagination, pagination =>
  pagination.get('current')
)

export const getOrganizationsPerPage = createSelector(getOrganizationsPagination, pagination =>
  pagination.get('perPage')
)

export const getOrganizationsSortCriteria = createSelector(getOrganizations, organizations => organizations.get('sort'))

export const getOrganizationsSortBy = createSelector(getOrganizationsSortCriteria, sort => sort.get('by'))

export const getOrganizationsSortDesc = createSelector(getOrganizationsSortCriteria, sort => sort.get('desc'))

export const getOrganizationsFilterBy = createSelector(getOrganizations, organizations => organizations.get('filter'))

export const getOrganizationFilteredList = createSelector(
  getOrganizationsList,
  getOrganizationsFilterBy,
  (list, filterBy) => {
    return filterBy && filterBy.length > 0
      ? list.filter(organization => {
          const name = organization.get('name') || ''
          const domain = organization.get('domain') || ''
          return `${name}${domain}`.indexOf(escapeSpecialSymbols(filterBy)) !== -1
        })
      : list
  }
)

export const getOrganizationFilteredSortedList = createSelector(
  getOrganizationFilteredList,
  getOrganizationsSortBy,
  getOrganizationsSortDesc,
  (list, by, desc) => {
    if (!by) return list

    if (by === 'activeUsers') {
      return list.sortBy(
        org => org.get('activeUsers'),
        (a, b) => {
          if (desc ? a > b : a < b) {
            return -1
          }
          if (desc ? a < b : a > b) {
            return 1
          }
          if (a === b) {
            return 0
          }
        }
      )
    }

    if (by === 'status') {
      return list.sortBy(org => org.get('active'), (a, b) => (desc ? b - a : a - b))
    }

    return list.sortBy(org => {
      const itemBy = org.get(by) || '--'
      return itemBy.toUpperCase()
    }, (a, b) => (desc ? a.localeCompare(b) : b.localeCompare(a)))
  }
)

export const getOrganizationsSortedListFilteredTotal = createSelector(
  getOrganizationFilteredSortedList,
  list => list.size
)

export const getOrganizationsTotalPages = createSelector(
  getOrganizationsSortedListFilteredTotal,
  getOrganizationsPerPage,
  (total, perPage) => Math.ceil(total / perPage)
)

export const getOrganizationsForCurrentPage = createSelector(
  getOrganizationFilteredSortedList,
  getOrganizationsCurrentPage,
  getOrganizationsPerPage,
  handleUsersForCurrentPage
)

export const getOrganizationUsers = createSelector(getOrganizations, organizations => organizations.get('users'))

export const getAllOrganizationUsers = createSelector(getOrganizationUsers, users => users.get('all'))

export const getOrganizationUsersSortBy = createSelector(getOrganizationUsers, users => users.getIn(['sort', 'by']))

export const getOrganizationUsersSortDesc = createSelector(getOrganizationUsers, users => users.getIn(['sort', 'desc']))

export const getOrganizationUsersFilterBy = createSelector(getOrganizationUsers, users => users.get('filter'))

export const getOrganizationUsersList = createSelector(getAllOrganizationUsers, users =>
  users.reduce((all, user) => {
    return all.push(user)
  }, List())
)

export const getOrganizationUsersFilterByTeam = createSelector(getOrganizationUsers, users => users.get('filterByTeam'))

export const getOrganizationUsersListFilteredByTeam = createSelector(
  getOrganizationUsersList,
  getOrganizationUsersFilterByTeam,
  filterByTeam
)

export const getOrganizationUsersNotInvitationAccepted = createSelector(
  getOrganizationUsersListFilteredByTeam,
  filterUsersNotInvitationAccepted
)

export const getOrganizationUsersNotInvitationAcceptedEmails = createSelector(
  getOrganizationUsersNotInvitationAccepted,
  takeEmailsUsersFromUsersList
)

const getOrganizationUsersNotHaveActiveProTrack = createSelector(
  getOrganizationUsersListFilteredByTeam,
  filterUsersNotProTrackPicked
)

export const getOrganizationUsersStoppedProTrack = createSelector(getOrganizationUsersNotHaveActiveProTrack, users => {
  return users.filter(user => user.get('isUserProtrackLevelsExist'))
})

export const getOrganizationUsersLevelFinished = createSelector(getOrganizationUsersNotHaveActiveProTrack, users => {
  return users.filter(user => user.get('levelFinished'))
})

export const getOrganizationUsersLevelStopped = createSelector(getOrganizationUsersNotHaveActiveProTrack, users => {
  return users.filter(user => user.get('levelStopped'))
})

export const getOrganizationUsersLevelFinishedEmails = createSelector(getOrganizationUsersLevelFinished, users =>
  users.reduce((users, user) => users.push(user.get('email')), List())
)

export const getOrganizationUsersLevelStoppedEmails = createSelector(getOrganizationUsersLevelStopped, users =>
  users.reduce((users, user) => users.push(user.get('email')), List())
)

export const getOrganizationUsersStoppedEmails = createSelector(getOrganizationUsersStoppedProTrack, users =>
  users.reduce((users, user) => users.push(user.get('email')), List())
)

export const getOrganizationUsersNotPickedProTrack = createSelector(
  getOrganizationUsersNotHaveActiveProTrack,
  users => {
    return users.filter(user => !user.get('isUserProtrackLevelsExist'))
  }
)

export const getOrganizationUsersNotProTrackPickedEmails = createSelector(
  getOrganizationUsersNotPickedProTrack,
  takeEmailsUsersFromUsersList
)

export const getOrganizationUsersNotProTrackPickedList = createSelector(
  getOrganizationUsersNotInvitationAccepted,
  users => users.reduce((all, user) => all.push(user), List())
)

export const getOrganizationUsersNotInvitationAcceptedList = createSelector(
  getOrganizationUsersNotPickedProTrack,
  users => users.reduce((all, user) => all.push(user), List())
)

export const getOrganizationUsersFilter = createSelector(getOrganizationUsers, users => users.get('usersFilter'))

const getOrganizationUsersListFilteredByStatus = createSelector(
  getOrganizationUsersListFilteredByTeam,
  getOrganizationUsersNotInvitationAcceptedList,
  getOrganizationUsersNotProTrackPickedList,
  getOrganizationUsersLevelFinished,
  getOrganizationUsersLevelStopped,
  getOrganizationUsersFilter,
  (list, listNotInvitationAccepted, listNotProTrackPicked, listCompletedProTrack, listPausedProTrack, byStatus) => {
    const filteredUsers =
      {
        [USERS_FILTER.POST]: listNotProTrackPicked,
        [USERS_FILTER.FIRE_OUTLINE]: listNotInvitationAccepted,
        [USERS_FILTER.FIRE_OUTLINE_COMPLETED_PROTRACK]: listCompletedProTrack,
        [USERS_FILTER.FIRE_OUTLINE_PAUSED_PROTRACK]: listPausedProTrack,
      }[byStatus] || list
    return filteredUsers
  }
)
export const getOrganizationUsersListFiltered = createSelector(
  getOrganizationUsersListFilteredByStatus,
  getOrganizationUsersFilterBy,
  (list, bySearch) => {
    return filterBySearh(list, bySearch)
  }
)

export const getOrganizationUsersListFilteredTotal = createSelector(getOrganizationUsersListFiltered, list => list.size)

export const getOrganizationUsersTab = createSelector(getOrganizationUsers, users => users.get('tab'))

export const getOrganizationUsersOrganizationId = createSelector(getOrganizationUsers, users =>
  users.get('selectedOrganizationId')
)

export const getOrganizationUsersOrganizationName = createSelector(getOrganizationUsers, users =>
  users.get('selectedOrganizationName')
)

const getOrganizationUsersSelectedOrganization = createSelector(getOrganizationUsers, users =>
  users.get('selectedOrganization')
)

export const isOrganizationDemoMode = createSelector(
  getAllOrganizations,
  getOrganizationUsersOrganizationId,
  (organizations, id) => id && organizations.getIn([id.toString(), 'demoMode'])
)

export const getOrganizationUsersSelectedOrganizationAvailableProTrackLevels = createSelector(
  getOrganizationUsersSelectedOrganization,
  selectedOrg => {
    return selectedOrg && selectedOrg.get ? selectedOrg.get('proTrackLevels') : List()
  }
)

export const getSelectedProTrackLevelForSelectedOrganization = proTrackLevelId =>
  createSelector(getOrganizationUsersSelectedOrganizationAvailableProTrackLevels, levels => {
    return proTrackLevelId ? levels.find(level => level.id === proTrackLevelId) : null
  })

export const getHistorySwitchProTrackLevels = createSelector(getOrganizationUsersSelectedOrganization, selectedOrg => {
  return selectedOrg && selectedOrg.get ? selectedOrg.get('historySwitchProTrackLevels') : List()
})

export const getOrganizationsUsersOrganization = createSelector(
  getAllOrganizations,
  getOrganizationUsersOrganizationId,
  getFirstOrganization,
  (organizations, id, organization) => (id && organizations.get(id.toString())) || organization
)

export const getOrganizationTeams = createSelector(
  getAllOrganizations,
  getOrganizationUsersOrganizationId,
  (organization, id) => {
    return organization.getIn([id && id.toString(), 'teams'], List())
  }
)

export const getOrganizationNameById = createSelector(
  getOrganizationUsersOrganizationId,
  getOrganizationsList,
  (id, organizations) => {
    const currentOrg = organizations.find(organization => organization.get('id') === id)
    if (currentOrg) {
      return currentOrg.get('name')
    }
    return null
  }
)

export const getCountriesCodesBySelectedOrganizationId = createSelector(
  getOrganizationUsersOrganizationId,
  getOrganizationsList,
  (id, organizations) => {
    const currentOrg = organizations.find(organization => organization.get('id') === id)
    if (currentOrg) {
      return currentOrg.get('countriesCodes')
    }
    return new CountriesCodes({ [USA_ISO]: true })
  }
)

export const getOrganizationUsersPagination = createSelector(getOrganizationUsers, users => users.get('pagination'))

export const getOrganizationUsersCurrentPage = createSelector(getOrganizationUsersPagination, pagination =>
  pagination.get('current')
)

export const getOrganizationUsersPerPage = createSelector(getOrganizationUsersPagination, pagination =>
  pagination.get('perPage')
)

export const getOrganizationUsersListFilteredTotalPages = createSelector(
  getOrganizationUsersListFilteredTotal,
  getOrganizationUsersPerPage,
  (total, perPage) => Math.ceil(total / perPage)
)

export const getOrganizationUsersSortedList = createSelector(
  getOrganizationUsersListFilteredByTeam,
  getOrganizationUsersSortBy,
  getOrganizationUsersSortDesc,
  (list, by, desc) => {
    if (by) {
      if (by === 'fullName') {
        return list.sortBy(item => {
          const initials = `${item.get('firstName')}${item.get('lastName')}` || '--'
          return initials.toUpperCase()
        }, (a, b) => (desc ? a.localeCompare(b) : b.localeCompare(a)))
      } else if (by === 'completedCount') {
        return list.sortBy(item => item.get('completedCount'), (a = 0, b = 0) => (desc ? b - a : a - b))
      } else if (by === 'email') {
        return list.sortBy(item => item.get('email'), (a, b) => (desc ? a.localeCompare(b) : b.localeCompare(a)))
      }
    }
    return list
  }
)

export const getOrganizationUsersListFilteredSorted = createSelector(
  getOrganizationUsersListFiltered,
  getOrganizationUsersSortBy,
  getOrganizationUsersSortDesc,
  filterBySort
)

export const getOrganizationUsersListFilteredSortedForCurrentPage = createSelector(
  getOrganizationUsersListFilteredSorted,
  getOrganizationUsersCurrentPage,
  getOrganizationUsersPerPage,
  (list, currentPage, perPage) => list.skip(currentPage * perPage).take(perPage)
)

export const getOrganizationUsersByUserRoleTotalPages = createSelector(
  getOrganizationUsersListFilteredTotal,
  getOrganizationUsersPerPage,
  (total, perPage) => Math.ceil(total / perPage)
)

const getOrganizationUsersListFilteredByAdminRole = createSelector(getOrganizationUsersSortedList, users =>
  users.filter(user => user && user.get && user.get('roles') && user.get('roles').includes(Map({ name: 'admin' })))
)

const getOrganizationUsersListFilteredByMultiAdminRole = createSelector(getOrganizationUsersSortedList, users =>
  users.filter(
    user => user && user.get && user.get('roles') && user.get('roles').includes(Map({ name: 'multi-admin' }))
  )
)

export const getOrganizationAdminsNotProTrackPicked = createSelector(
  getOrganizationUsersListFilteredByAdminRole,
  filterUsersNotProTrackPicked
)

export const getOrganizationMultiAdminsNotProTrackPicked = createSelector(
  getOrganizationUsersListFilteredByMultiAdminRole,
  filterUsersNotProTrackPicked
)

export const getOrganizationAdminsNotProTrackPickedEmails = createSelector(
  getOrganizationAdminsNotProTrackPicked,
  takeEmailsUsersFromUsersList
)

export const getOrganizationMultiAdminsNotProTrackPickedEmails = createSelector(
  getOrganizationMultiAdminsNotProTrackPicked,
  takeEmailsUsersFromUsersList
)

export const getOrganizationAdminsNotInvitationAccepted = createSelector(
  getOrganizationUsersListFilteredByAdminRole,
  filterUsersNotInvitationAccepted
)

export const getOrganizationMultiAdminsNotInvitationAccepted = createSelector(
  getOrganizationUsersListFilteredByMultiAdminRole,
  filterUsersNotInvitationAccepted
)

export const getOrganizationAdminsNotInvitationAcceptedEmails = createSelector(
  getOrganizationAdminsNotInvitationAccepted,
  takeEmailsUsersFromUsersList
)

export const getOrganizationMultiAdminsNotInvitationAcceptedEmails = createSelector(
  getOrganizationMultiAdminsNotInvitationAccepted,
  takeEmailsUsersFromUsersList
)

export const getOrganizationAdminsListFiltered = createSelector(
  getOrganizationUsersListFilteredByAdminRole,
  getOrganizationAdminsNotInvitationAccepted,
  getOrganizationAdminsNotProTrackPicked,
  getOrganizationUsersFilterBy,
  getOrganizationUsersFilter,
  handleAdminsListFiltered
)

export const getOrganizationMultiAdminsListFiltered = createSelector(
  getOrganizationUsersListFilteredByMultiAdminRole,
  getOrganizationMultiAdminsNotInvitationAccepted,
  getOrganizationMultiAdminsNotProTrackPicked,
  getOrganizationUsersFilterBy,
  getOrganizationUsersFilter,
  handleAdminsListFiltered
)

export const getOrganizationUsersByAdminRoleTotalPages = createSelector(
  getOrganizationAdminsListFiltered,
  getOrganizationUsersPerPage,
  handleTotalPages
)

export const getOrganizationUsersByMultiAdminRoleTotalPages = createSelector(
  getOrganizationMultiAdminsListFiltered,
  getOrganizationUsersPerPage,
  handleTotalPages
)

export const getOrganizationUsersByAdminRoleForCurrentPage = createSelector(
  getOrganizationAdminsListFiltered,
  getOrganizationUsersCurrentPage,
  getOrganizationUsersPerPage,
  handleUsersForCurrentPage
)

export const getOrganizationUsersByMultiAdminRoleForCurrentPage = createSelector(
  getOrganizationMultiAdminsListFiltered,
  getOrganizationUsersCurrentPage,
  getOrganizationUsersPerPage,
  handleUsersForCurrentPage
)

export const getOrganizationsForm = createSelector(getOrganizations, organizations => organizations.get('form'))

export const getOrganizationsFormType = createSelector(getOrganizationsForm, form => form.get('type'))

export const getOrganizationFormLevels = createSelector(getPublishedProtracksList, protracks =>
  Map(
    protracks.reduce((levels, protrack) => {
      const updatedLevels = protrack.get('levels').reduce((protrackLevels, level) => {
        protrackLevels[level.get('id')] = Map({
          id: level.get('id'),
          customName: level.get('name'),
          selected: false,
          hasActiveUsers: false,
        })
        return protrackLevels
      }, {})
      return { ...levels, ...updatedLevels }
    }, {})
  )
)

 const getOrganizationFormLibrariesLevels = createSelector(getPublishedProTracksLibrariesList, proTracks => {
   const res = proTracks.reduce((levels, proTrack) => {
     const updatedLevels = proTrack.get('libraryLevels').reduce((proTrackLevels, level) => {
       proTrackLevels[level.get('id')] = Map({
         id: level.get('id'),
         customName: level.get('name'),
         selected: false,
       })
       return proTrackLevels
     }, {})
     return { ...levels, ...updatedLevels }
   }, {})
   return Map(res)
 }
)

export const getOrganizationCreateFormValues = createSelector(getOrganizationFormLevels, getOrganizationFormLibrariesLevels, (proTracksLevels, proTracksLibrariesLevels) => {
  return Map({
    microactions: false,
    hasLevelWithActiveUsers: false,
    hasIrregularWorkWeek: false,
    canRepeatActivity: true,
    proTrackLevelEndsByActivityCount: false,
    proTracksLevels,
    social: false,
    sms: false,
    stories: false,
    reactions: false,
    peerInvites: false,
    feedbackReviews: false,
    reviews: false,
    reviewsLinks: new ReviewsLinks(),
    countriesCodes: new CountriesCodes({ [USA_ISO]: true }),
    changeNotificationFrequency: false,
    proTracksLibrariesLevels,
  })
})

export const getEditingOrganizationFromRouter = createSelector(
  getAllOrganizations,
  editingOrganizationIdFromRouter,
  (organizations, id) => organizations.get(id.toString())
)

export const getEditingOrganizationFormValues = createSelector(
  getEditingOrganizationFromRouter,
  getOrganizationFormLevels,
  getOrganizationFormLibrariesLevels,
  (organization, microActionLevels, libraryLevels) => {
    const updatedMicroActionLevels = organization.get('proTracksLevels').reduce((levels, orgLevel) => {
      return levels.set(
        orgLevel.get('id').toString(),
        Map({
          id: orgLevel.get('id'),
          customName: orgLevel.get('name'),
          selected: true,
          hasActiveUsers: orgLevel.get('hasActiveUsers'),
          isOrganizationProTrackLevelActive: orgLevel.get('isOrganizationProTrackLevelActive'),
          teams: orgLevel.get('teams'),
        })
      )
    }, microActionLevels)
    const updatedLibrariesLevels = organization.get('proTracksLibrariesLevels').reduce((levels, orgLevel) => {
      return levels.set(
        orgLevel.get('id').toString(),
        Map({
          id: orgLevel.get('id'),
          customName: orgLevel.get('name'),
          selected: true,
        })
      )
    }, libraryLevels)
    return Map({
      ...organization.toJS(),
      countriesCodes: Map(organization.get('countriesCodes')),
      inactiveNotificationsPeriod: organization.get('inactiveNotificationsPeriod'),
      microactions: organization.get('microactions'),
      hasLevelWithActiveUsers: !!organization.get('proTracksLevels').find(level => level.get('hasActiveUsers')),
      proTracksLevels: updatedMicroActionLevels,
      proTracksLibrariesLevels: updatedLibrariesLevels,
    })
  }
)

export const getOrganizationFormSprints = createSelector(getEditingOrganizationFromRouter, organization => {
  return organization && organization.get ? organization.get('sprints') : List()
})

export const getOrganizationsFormDisabled = createSelector(getOrganizationsForm, form => form.get('disabled'))

export const getViewOrganizationSettings = createSelector(getOrganizations, organizations =>
  organizations.get('viewOrganizationSettings')
)

export const getViewOrganizationSettingsLoaded = createSelector(getViewOrganizationSettings, settings =>
  settings.get('loaded')
)

export const getViewOrganizationSettingsEntity = createSelector(getViewOrganizationSettings, settings =>
  settings.get('entity')
)

export const getViewOrganizationSettingsId = createSelector(getViewOrganizationSettingsEntity, entity =>
  entity.get('id')
)

export const getViewOrganizationTeams = createSelector(
  getAllOrganizations,
  getViewOrganizationId,
  (organization, id) => {
    return organization.getIn([id && id.toString(), 'teams'], List())
  }
)

export const getViewOrganizationSettingsName = createSelector(getViewOrganizationSettingsEntity, entity =>
  entity.get('name')
)

export const getViewOrganizationSettingsDomain = createSelector(getViewOrganizationSettingsEntity, entity =>
  entity.get('domain')
)

export const getViewOrganizationSettingsTotalUsers = createSelector(
  getViewOrganizationSettingsEntity,
  entity => entity.get('users').length
)

export const getViewOrganizationSettingsLogo = createSelector(getViewOrganizationSettingsEntity, entity =>
  entity.get('logo')
)

/**
 * @see store/records/organization OneSignal
 * @return {OneSignal}
 */
export const oneSignal = createSelector(getEditingOrganizationFromRouter, org => org.get('oneSignal'))

const getIsPending = createSelector(getOrganizations, org => org.get('isPending'))

export const isPendingSprint = createSelector(getIsPending, isPending => isPending.get('sprint'))

export const getViewUser = createSelector(getOrganizationUsers, users => users.get('user'))

export const getViewUserId = createSelector(getViewUser, user => user && user.get('view'))

export const getViewUserNameOrEmail = createSelector(getAllOrganizationUsers, getViewUserId, (users, id) => {
  const user = users.get(id && id.toString())
  return user && getUserNameOrEmail(user.get('firstName'), user.get('lastName'), user.get('email'))
})

export const getViewUserProtracks = createSelector(getViewUser, user => user && user.get('protracks'))

export const getUserResendNotificationStatus = createSelector(
  getViewUser,
  user => user && user.get('resendNotificationStatus')
)

export const getViewUserPending = createSelector(getOrganizationUsers, users => users.get('pending'))

export const getViewUserLoaded = createSelector(getOrganizationUsers, users => users.get('loaded'))

export const getViewUserActivities = createSelector(getViewUser, user => user && user.get('activities'))

export const getViewUserLevelName = createSelector(getViewUser, user => user.get('levelName'))

export const getViewUserPublicProTrackName = createSelector(getViewUser, user => user.get('publicProTrackName'))

export const isSMSAvailableForOrganization = createSelector(
  getAllOrganizations,
  getOrganizationUsersOrganizationId,
  (organization, id) => id && organization.getIn([id.toString(), 'sms'])
)

export const isReviewsAvailableForOrganization = createSelector(
  getOrganizationsUsersOrganization,
  organization => (organization ? organization.get('reviews') : false)
)

export const isMicroActionsAvailableForOrganization = createSelector(
  getOrganizationsUsersOrganization,
  organization => (organization ? organization.get('microactions') : false)
)

export const isViewOrganizationReviewsLinksFullfilled = createSelector(
  getViewOrganizationReviewsLinks,
  reviewsLinks => (reviewsLinks ? reviewsLinks.some(link => typeof link === 'string') : false)
)

export const getInvites = createSelector(getOrganizations, organizations => {
  return organizations.get('invites') || invitesState
})

export const getInvitesOrganization = createSelector(getInvites, invites => {
  return invites.get('organization')
})

export const getInvitesOrganizationTeams = createSelector(getInvitesOrganization, organization => {
  return organization.get('teams')
})

export const getUsedLevelsIdsList = createSelector(getOrganizations, organizations => {
  return organizations.get('usedLevelsIds')
})

export const getHasActiveUsersLevelsIdsList = createSelector(getOrganizations, organizations => {
  return organizations.get('hasActiveUsersLevelsIds')
})

export const getOrganizationTeamsById = organizationId =>
  createSelector(getAllOrganizations, organizations => {
    return organizations.getIn([organizationId.toString(), 'teams'])
  })

export const getAvailableLanguages = createSelector(getOrganizations, organizations =>
  Object.entries(organizations.get('languages'))
)

export const getOrganizationChildrenList = createSelector(
  [getOrganizationSettingsChildrenList, getAllOrganizations],
  (organizationsIds, allOrganization) => {
    if (organizationsIds.size === 0) {
      return List()
    }
    return organizationsIds.reduce((all, orgId) => {
      return all.push(allOrganization.get(orgId.toString()))
    }, List())
  }
)

export const getOrganizationProtrackLevelChangesByOrg = createSelector(
  getEditingOrganizationFromRouter,
  organization => (organization ? organization.get('levelsChanges') : Map())
)

export const getPartnersLinksByOrganizationId = createSelector(getEditingOrganizationFromRouter, organization => {
  return organization ? organization.get('partnersLinks') : List()
})

export const getOrganizationsListForSuperAdmin = createSelector(getOrganizationsList, organizations =>
  organizations.unshift(
    Map({ id: ACTIVITY_DEFAULT_IMAGE_MESSAGE_ID, domain: i18next.t(`admin:${ACTIVITY_DEFAULT_IMAGE_MESSAGE}`) }),
    Map({ id: ACTIVITY_DEFAULT_IMAGE_DOMAIN_ID, domain: ACTIVITY_DEFAULT_IMAGE_DOMAIN })
  )
)

export const getOrganizationsListForMultiAdmin = createSelector(getOrganizationsListForSuperAdmin, organizations =>
  organizations.delete(1)
)
